/**
 * Contextual action prompts — a small floating badge stack that TRACKS the
 * player on-screen and shows only the situational actions available right now:
 * loot a corpse, take/leave cover, climb over cover, fire, melee/takedown.
 *
 * It deliberately does NOT show movement or camera keys (those are always
 * available and would be noise) — only affordances that blink in and out as the
 * player's surroundings change, teaching the controls in context.
 *
 * DOM overlay positioned from a screen-space point the caller computes with the
 * same rotation-aware transform the renderer draws with (see main.ts). No
 * PixiJS here.
 */

/** A single available action: the key to press and what it does. */
export interface ActionPrompt {
  /** Key cap text, e.g. 'E', 'X', 'W', 'Space'. */
  key: string;
  /** Human label, e.g. 'Loot', 'Take cover'. */
  label: string;
  /** Optional accent (hex) for emphasis — e.g. a red takedown. */
  accent?: string;
}

export class ActionPrompts {
  readonly root: HTMLElement;
  private lastSig = '';

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'action-prompts';
    this.root.style.display = 'none';
    host.appendChild(this.root);
  }

  /**
   * @param screenX,screenY  the player's on-screen pixel position (badge stack
   *   floats just above it).
   * @param prompts  the currently-available actions (empty → hidden).
   */
  update(screenX: number, screenY: number, prompts: readonly ActionPrompt[]): void {
    if (prompts.length === 0) {
      this.root.style.display = 'none';
      this.lastSig = '';
      return;
    }
    this.root.style.display = 'flex';
    this.root.style.left = `${screenX}px`;
    this.root.style.top = `${screenY}px`;
    // Only rebuild the DOM when the set of prompts actually changes — the
    // position updates every frame but the content usually doesn't.
    const sig = prompts.map((p) => `${p.key}:${p.label}:${p.accent ?? ''}`).join('|');
    if (sig === this.lastSig) return;
    this.lastSig = sig;
    this.root.innerHTML = prompts
      .map((p) => {
        const accent = p.accent ? ` style="color:${p.accent};border-color:${p.accent}"` : '';
        return `<div class="ap-row"><span class="ap-key"${accent}>${p.key}</span><span class="ap-label">${p.label}</span></div>`;
      })
      .join('');
  }
}
